import { useEffect, useRef, useState } from 'react';
import { useManualPricePreview } from './useManualPricePreview';
import type { PropertyResponse } from '@/shared/api';
import { availabilityState } from './AvailabilityCard';
import { buildManualBookingRequest, nightsBetween, plusDaysIso, todayIso } from './helpers';
import { canSubmitManualBooking, EMPTY_GUEST, type GuestFormValues } from './manualBookingRules';
import { useAvailability } from './useAvailability';
import { useManualBooking } from './useManualBooking';
import { useApi } from '@/shared/api';
import { useAuth } from '@/shared/auth';
import { useQueryClient } from '@tanstack/react-query';

export type PanelApartment =
  | { property: Pick<PropertyResponse, 'id' | 'name' | 'adminDisplayName' | 'currency'> }
  | { choices: PropertyResponse[] };

/** Zustand der manuellen Buchung: Eingaben, Prüfung, Anlegen und was daraus folgt. */
export function useManualBookingForm(args: {
  open: boolean;
  apartment: PanelApartment;
  prefillFrom?: string;
  prefillTo?: string;
  onCreated: () => void;
}) {
  const { open, apartment, prefillFrom, prefillTo } = args;
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const onCreatedRef = useRef(args.onCreated);
  onCreatedRef.current = args.onCreated;
  const { user } = useAuth();
  const qc = useQueryClient();
  const operationKey = `stayon.manual-booking.${user?.tenantId ?? 'unknown'}.${user?.userId ?? 'unknown'}`;
  const operation = useRef(sessionStorage.getItem(operationKey) ?? crypto.randomUUID());
  const [unresolved, setUnresolved] = useState(() => sessionStorage.getItem(operationKey) !== null);
  const [reconciling, setReconciling] = useState(false);
  const choices = 'choices' in apartment ? apartment.choices : null;
  const [chosenId, setChosenId] = useState('');
  const property =
    'property' in apartment ? apartment.property : choices?.find((p) => p.id === chosenId);
  const [from, setFrom] = useState(prefillFrom ?? todayIso);
  const [to, setTo] = useState(() => prefillTo ?? plusDaysIso(prefillFrom ?? todayIso(), 2));
  const [guest, setGuest] = useState<GuestFormValues>(EMPTY_GUEST);
  const [override, setOverride] = useState(false);

  // Jedes Öffnen beginnt leer: vorher standen die Felder der letzten Buchung noch da.
  useEffect(() => {
    if (!open) return;
    setFrom(prefillFrom ?? todayIso());
    setTo(prefillTo ?? plusDaysIso(prefillFrom ?? todayIso(), 2));
    setGuest(EMPTY_GUEST);
    setOverride(false);
    setChosenId('');
  }, [open, prefillFrom, prefillTo]);

  useEffect(() => {
    if (!open || !unresolved) return;
    let active = true;
    setReconciling(true);
    const checkedOperation = operation.current;
    void apiRef.current.bookings
      .getManualOperation(checkedOperation)
      .then(() => {
        if (!active || sessionStorage.getItem(operationKey) !== checkedOperation) return;
        sessionStorage.removeItem(operationKey);
        operation.current = crypto.randomUUID();
        setUnresolved(false);
        void qc.invalidateQueries({ queryKey: ['bookings'] });
        void qc.invalidateQueries({ queryKey: ['calendar'] });
        onCreatedRef.current();
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setReconciling(false);
      });
    return () => {
      active = false;
    };
  }, [open, unresolved, operationKey, qc]);

  const nights = nightsBetween(from, to);
  const rate = Number(guest.nightlyRate) || 0;
  const datesValid = nights > 0;
  const propertyId = property?.id ?? '';
  const avail = useAvailability({
    propertyId,
    from,
    to,
    enabled: open && datesValid && Boolean(property),
  });
  const create = useManualBooking({
    onSuccess: (submittedOperation) => {
      if (sessionStorage.getItem(operationKey) !== submittedOperation) return false;
      sessionStorage.removeItem(operationKey);
      setUnresolved(false);
      operation.current = crypto.randomUUID();
      args.onCreated();
      return true;
    },
  });
  const mode = guest.mode ?? 'PaymentLink';
  const priceBasis = guest.priceBasis ?? 'PerNight';
  const quoteInput = {
    propertyId,
    mode,
    basis: priceBasis,
    amount: rate,
    checkInDate: from,
    checkOutDate: to,
    adults: guest.adults,
    children: guest.children,
  };
  const quote = useManualPricePreview(
    quoteInput,
    open && datesValid && Boolean(property) && guest.nightlyRate.trim() !== '' && rate >= 0,
  );
  useEffect(() => setOverride(false), [propertyId, from, to]);
  const state = availabilityState({
    loading: avail.isLoading,
    unavailable: avail.isUnavailable,
    hard: avail.hardConflicts.length,
    soft: avail.softConflicts.length,
  });
  // Ohne Antwort der Prüfung wird nicht angelegt: Mindestnächte und Sperren kennt nur sie.
  const canSubmit =
    Boolean(property) &&
    !reconciling &&
    quote.data !== undefined &&
    !quote.isFetching &&
    !quote.isError &&
    avail.data !== undefined &&
    canSubmitManualBooking({
      datesValid,
      guest,
      rate,
      availabilityUnknown: avail.isUnavailable,
      hardConflicts: avail.hardConflicts,
      softConflicts: avail.softConflicts,
      override,
    });
  return {
    choices,
    quote,
    mode,
    priceBasis,
    property,
    propertyId,
    setChosenId,
    from,
    to,
    setFrom,
    setTo,
    guest,
    setField: <K extends keyof GuestFormValues>(field: K, value: GuestFormValues[K]) =>
      setGuest((current) => ({ ...current, [field]: value })),
    override,
    setOverride,
    nights,
    rate,
    datesValid,
    avail,
    create,
    state,
    canSubmit,
    unresolved,
    /** Etwas eingegeben, das beim Schließen verloren ginge. */
    dirty: chosenId !== '' || JSON.stringify(guest) !== JSON.stringify(EMPTY_GUEST),
    submit: () => {
      const body = {
        ...buildManualBookingRequest({ propertyId, from, to, guest, nightlyRate: rate }),
        mode,
        priceBasis,
        agreedAmount: rate,
        expectedPriceIdentity: quote.data?.identity,
      };
      sessionStorage.setItem(operationKey, operation.current);
      setUnresolved(true);
      create.mutate({ ...body, operationId: operation.current });
    },
  };
}
